'use client';

import { createClient } from '@/lib/supabase/client';

export const GET_SESSION_TIMEOUT_MS = 5000;
export const DEFAULT_FETCH_TIMEOUT_MS = 15000;

let authBusyUntil = 0;
const idleListeners: Array<() => void> = [];
let visibilityListenerAttached = false;

/** Mark auth as busy (e.g. tab became visible, token refresh likely). */
export function notifyAuthBusy(durationMs = 1200) {
  authBusyUntil = Date.now() + durationMs;
}

/** Mark auth idle after TOKEN_REFRESHED or similar. */
export function notifyAuthIdle() {
  authBusyUntil = 0;
  while (idleListeners.length > 0) {
    const next = idleListeners.shift();
    next?.();
  }
}

function attachVisibilityListener() {
  if (visibilityListenerAttached || typeof document === 'undefined') return;
  visibilityListenerAttached = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      notifyAuthBusy(1200);
    }
  });
}

attachVisibilityListener();

async function waitForAuthIdle(maxMs = 8000): Promise<void> {
  const remaining = authBusyUntil - Date.now();
  if (remaining <= 0) return;

  await Promise.race([
    new Promise<void>((resolve) => {
      idleListeners.push(resolve);
    }),
    new Promise<void>((resolve) => setTimeout(resolve, Math.min(remaining, maxMs))),
    new Promise<void>((_, reject) =>
      setTimeout(() => reject(new Error('Auth wait timeout')), maxMs)
    ),
  ]).catch(() => {
    /* proceed even if wait times out */
  });
}

export function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  message = 'Request timed out. Please refresh and try again.'
): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(message)), ms)
    ),
  ]);
}

/** Wait until tab-focus token refresh can finish, then resolve session without blocking auth lock. */
export async function ensureSessionReady(): Promise<void> {
  await waitForAuthIdle();
  const supabase = createClient();
  const { error } = await withTimeout(
    supabase.auth.getSession() as Promise<Awaited<ReturnType<typeof supabase.auth.getSession>>>,
    GET_SESSION_TIMEOUT_MS,
    'Session initialization timed out. Please try again.'
  );
  if (error) throw error;
}

/** Run a data fetch after session is ready, with a timeout. */
export async function runProtectedQuery<T>(
  fn: () => Promise<T>,
  timeoutMs = DEFAULT_FETCH_TIMEOUT_MS
): Promise<T> {
  await ensureSessionReady();
  return withTimeout(fn(), timeoutMs);
}
